import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api, fmtMoney, fmtDate } from '../lib/api.js';

export default function Reimbursements() {
  const { isOwner, user } = useOutletContext();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [method, setMethod] = useState('venmo');
  const [paymentRef, setPaymentRef] = useState('');

  async function load() {
    try { setData(await api.get('/api/reimbursements')); } catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!data) return <div className="text-ink-700/60 italic">Loading…</div>;

  function toggle(id) {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  }

  async function markPaid() {
    if (selected.size === 0) return;
    await api.post('/api/reimbursements/mark-paid', {
      receipt_ids: [...selected],
      method,
      payment_ref: paymentRef.trim() || null,
    });
    setSelected(new Set());
    setPaymentRef('');
    load();
  }

  async function unflag(r) {
    if (!confirm(`Mark "${r.vendor}" (${fmtMoney(r.total_cents)}) as NOT reimbursable?\n\nThe receipt stays in your books, but the personal-card flag is cleared (it becomes a farm-card receipt and disappears from this list).`)) return;
    try { await api.patch(`/api/expenses/${r.id}`, { paid_by_user_id: null }); load(); }
    catch (e) { setErr(e.message); }
  }

  async function deleteReceipt(r) {
    if (!confirm(`Delete the entire receipt "${r.vendor}" (${fmtMoney(r.total_cents)})?\n\nGone from books permanently. If you only want to clear the reimbursable flag, choose "Not reimbursable" instead.`)) return;
    try { await api.delete(`/api/expenses/${r.id}`); load(); }
    catch (e) { setErr(e.message); }
  }

  const totals = data.totals_by_user_cents || {};
  const grandTotal = data.receipts.reduce((acc, r) => acc + r.total_cents, 0);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-serif text-olive-900">Reimbursements</h1>
        <p className="text-sm text-ink-700/70">
          {isOwner ? 'Personal-card receipts owed back to users.' : `Receipts you paid on your personal card. Farm owes you ${fmtMoney(grandTotal)}.`}
        </p>
      </header>

      {Object.keys(totals).length > 0 && isOwner && (
        <div className="card">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-700/70 mb-2">Owed by user</h2>
          <ul className="space-y-1">
            {Object.entries(totals).map(([name, cents]) => (
              <li key={name} className="flex justify-between text-sm">
                <span>{name}</span>
                <span className="font-medium text-rust-700">{fmtMoney(cents)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.receipts.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-ink-700/70">No outstanding reimbursements.</p>
        </div>
      ) : (
        <>
          <ul className="space-y-2">
            {data.receipts.map(r => (
              <li key={r.id} className={`card ${selected.has(r.id) ? 'ring-2 ring-olive-500' : ''}`}>
                <div className="flex items-start gap-3">
                  {isOwner && (
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                  )}
                  <div className="flex-1">
                    <div className="font-medium text-ink-900">{r.vendor}</div>
                    <div className="text-sm text-ink-700/70">{fmtDate(r.date)} · paid by {r.paid_by_name}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="text-lg font-semibold text-rust-700">{fmtMoney(r.total_cents)}</div>
                    {isOwner && (
                      <div className="flex gap-2 text-xs">
                        <button onClick={() => unflag(r)} className="btn-ghost text-xs py-0.5 px-2">
                          Not reimbursable
                        </button>
                        <button onClick={() => deleteReceipt(r)} className="text-xs text-rust-700 hover:underline px-1">
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {isOwner && selected.size > 0 && (
            <div className="card sticky bottom-4 z-10 bg-cream-100">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="label">Method</label>
                  <select className="input w-32" value={method} onChange={e => setMethod(e.target.value)}>
                    {['venmo', 'zelle', 'cash', 'check', 'card', 'other'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <label className="label">Payment ref</label>
                  <input className="input" value={paymentRef} onChange={e => setPaymentRef(e.target.value)} placeholder="Venmo txn id / check #" />
                </div>
                <button onClick={markPaid} className="btn-primary">
                  Mark {selected.size} paid
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
